const en = {
  navigation: {
    button: "Add new goal",
    vision: "Vision",
    mobile: {
      home: "Home",
      goals: "Goals",
      calendar: "Calendar",
      board: "Board",
      wishlist: "Wishlist",
      settings: "Settings",
    },
  },
  homepage: {
    headline: "Dashboard",
    description: "An organised view of your year - all in one place",
  },
  vision_board: {
    headline: "Vision board",
    description:
      "Design your year's vision by arranging images tied to your goals",
    download: {
      button: "Download",
      preparing: "Preparing image for download...",
      success: "Vision board downloaded successfully!",
      error: "Failed to download",
      element_not_found: "Vision board element not found.",
    },
    aspect_ratio: {
      label: "Aspect ratio:",
      video: "16:9",
      landscape: "4:3",
      square: "1:1",
      portrait: "3:4",
    },
  },
  goals: {
    headline: "Goals",
    description: "A clear view of where you're headed and how far you've come",
    not_found: "Goal not found",
    back_to_goals: "Back to goals",
    details_headline: "All in one place",
    details_description: "See the bigger picture, from vision to progress",
    edit_goal: "Edit goal",
    details: {
      category: "Category",
      year: "Year",
      deadline: "Deadline",
      progress: "Progress",
    },
    loading: "Loading...",
    chart: {
      completed: "Completed",
      incomplete: "Incomplete",
    },
    countdown: {
      days_left: "days left",
    },
    details_page: {
      milestones_title: "Milestones",
      milestone_stats_title: "Progress",
      milestone_stats_description:
        "Total milestones, completed and remaining milestones",
      milestones_description: "Tasks that move your goal forward",
      goal_description: "Insight into your journey so far",
      countdown_title: "Deadline",
      countdown_description: "Time remaining to bring your goal to life",
      stats: {
        completed: "completed",
        total: "total",
        remaining: "upcoming",
      },
      countdown: {
        days: "days",
        deadline_label: "Deadline:",
      },
    },
  },
  milestones: {
    headline: "Calendar",
    description: "Stay connected to your progress and achievements each day",
    loading: "Loading...",
  },
  wishlist: {
    headline: "Wishlist",
    description: "Add your wishes, celebrate progress, and share with others",
    share: {
      button: "Share",
      success: "Wishlist link copied!",
      error: "Failed to generate public link.",
    },
    add: {
      success: "Wish added successfully!",
      error: "Failed to add wish.",
      error_generic: "Error adding wish",
    },
  },
  notification: {
    headline: "Notifications",
    description: "Thoughtful reminders to support your progress",
    empty: "No updates at the moment",
    loading: "Loading...",
    popups: {
      welcome: {
        title: "Welcome to Niumi!",
        message:
          'We\'re glad you\'re here. Your journey starts now, and we\'re here to support every step. Explore our <a href="https://www.niumi.dk/blog/" target="_blank" rel="noopener noreferrer" class="text-primary underline">blog</a>, browse the <a href="https://www.niumi.dk/glossary/" target="_blank" rel="noopener noreferrer" class="text-primary underline">glossary</a> for clarity, or visit the <a href="https://www.niumi.dk/help-center/" target="_blank" rel="noopener noreferrer" class="text-primary underline">help center</a> if you have questions. Looking for more inspiration? <a href="https://www.niumi.dk/newsletter/" target="_blank" rel="noopener noreferrer" class="text-primary underline">Join the newsletter</a> to stay connected.',
        button_primary: "Add your first goal",
        button_secondary: "Close",
      },
      daily_milestone: {
        title: "Daily reminder",
        message:
          "You have {count} {tasks} scheduled for {date}, connected to {goalsCount} {goals}.",
        tasks_one: "task",
        tasks_other: "tasks",
        goals_one: "goal",
        goals_other: "goals",
        focus_title: "Today's focus:",
        motivation:
          "Each step you take today moves you closer to a better version of yourself. Trust the process!",
        button_primary: "Go to calendar",
        button_secondary: "Close",
      },
      goal_completed: {
        title: "Congratulations! Goal completed!",
        message: "Congratulations on completing your goal!",
        details_title: "Goal details:",
        milestones_completed: "Completed {count} milestones",
        completed_on: "Completed on {date}",
        celebration:
          "Take a moment to celebrate this achievement. Your dedication and hard work have paid off!",
        button_primary: "View goal",
        button_secondary: "Close",
      },
    },
  },
  profile: {
    headline: "Settings",
    description: "Manage your preferences and customise your experience",
    tabs: {
      personal: "Account details",
      login: "Security",
      subscription: "Subscription",
      notifications: "Resources",
      security: "Delete account",
      data_privacy: "Data & privacy",
    },
    tab_1: "Account details",
    tab_1_description:
      "Update your profile, contact information, and preferences to customize your experience.",
    tab_2: "Subscription",
    tab_2_description: "Manage your subscription and payment settings.",
    tab_3: "Security",
    tab_3_description: "Manage your account security settings.",
    tab_4: "Resources",
    tab_4_description:
      "Manage your notification preferences and communication settings.",
    tab_5: "Delete account",
    tab_5_description: "Deleting your account is a permanent action.",
    tab_6: "Data & privacy",
    tab_6_description: "Control your data and privacy settings.",
    card_personal_title: "Personal info",
    personal: {
      form: {
        label_name: "First name",
        label_surname: "Surname",
        label_country: "Country",
        characters: "characters",
      },
    },
    cards: {
      change_email: {
        title: "Change email",
        description: "Change your account email.",
        current: "Current email:",
      },
      delete_account: {
        title: "Delete account",
        description: "Deleting your account is a permanent action.",
      },
      current_plan: {
        title: "Current plan",
      },
      cancel_subscription: {
        title: "Cancel subscription",
        description:
          "Cancel your subscription at any time. You'll continue to have access until the end of your billing period.",
        description_canceled:
          "Your subscription has been canceled on {date}. You'll continue to have access until the end of your billing period.",
      },
      security: {
        add_password_title: "Add password",
        add_password_description:
          "Add a password to your account to enable email login.",
        update_password_title: "Update password",
        update_password_description:
          "Your details shape how the app feels. Update your name, surname, country, or email anytime.",
        confirm_new_password: "Confirm new password",
      },
      resources: {
        subheadline: "Resources",
      },
    },
    form: {
      first_name: "First name",
      surname: "Surname",
      country: "Country",
      email: "Email",
      current_password: "Current password",
      new_password: "New password",
      confirm_password: "Confirm password",
      button_update: "Update",
      button_delete: "Delete account",
      button_cancel: "Cancel subscription",
      button_subscribe: "Subscribe",
      button_manage: "Manage subscription",
      button_change_email: "Change email",
      button_update_password: "Update password",
      button_delete_account: "Delete account",
      button_manage_subscription: "Manage subscription",
      button_cancel_subscription: "Cancel subscription",
      button_renew_subscription: "Renew subscription",
      button_upgrade_to_premium: "Upgrade to premium",
      button_subscribe_now: "Subscribe now",
      title_change_email: "Change your email",
      description_change_email: "Enter your new email address below.",
      button_confirm: "Confirm",
      title_update_password: "Update your password",
      description_update_password: "Enter your current and new password below.",
      title_complete_subscription: "Complete your subscription",
      description_complete_subscription:
        "Enter your payment details to complete your subscription.",
      title_manage_subscription: "Manage your subscription",
      description_manage_subscription:
        "Manage your subscription details and payment methods.",
      title_cancel_subscription: "Cancel your subscription",
      description_cancel_subscription:
        "Are you sure you want to cancel your subscription? You'll continue to have access until the end of your billing period.",
      button_manage_payment_methods: "Manage payment methods",
      text_subscription_portal:
        "You can manage your subscription and payment methods here.",
      characters: "characters",
      email_verified: "Verified",
      email_not_verified: "Not verified",
    },
    resources: {
      blog: {
        title: "Blog",
        description: "Ideas, insights, and inspiration for your growth journey",
        button: "Visit blog",
      },
      glossary: {
        title: "Glossary",
        description: "Learn about key terms and concepts",
        button: "View glossary",
      },
      help_center: {
        title: "Help center",
        description: "Get support and find answers to your questions",
        button: "Go to help center",
      },
      newsletter: {
        title: "Newsletter",
        description: "Subscribe to our newsletter for updates and tips",
        button: "Subscribe to newsletter",
      },
    },
    notifications: {
      email_notifications: {
        title: "Email Notifications",
        description: "Choose which emails you want to receive from us.",
      },
      push_notifications: {
        title: "Push Notifications",
        description: "Control push notifications for your mobile device.",
        new_content_alerts: {
          title: "New content alerts",
          description: "Get notified when new content is available",
        },
        reminder_notifications: {
          title: "Reminder notifications",
          description: "Receive reminders about your goals and progress",
        },
        marketing_communications: {
          title: "Marketing communications",
          description: "Receive updates about new features and offers",
        },
      },
    },
    security: {
      two_factor_auth: {
        title: "Two-Factor Authentication",
        description: "Add an extra layer of security to your account.",
        sms_auth: {
          title: "SMS Authentication",
          description: "Receive codes via text message",
          button: "Enable",
        },
        authenticator_app: {
          title: "Authenticator App",
          description: "Use apps like Google Authenticator or Authy",
          button: "Enable",
        },
      },
      login_history: {
        title: "Login History",
        description: "Review recent login activity on your account.",
        current_session: "Current Session",
        previous_session: "Previous Session",
        active_now: "Active now",
        current: "Current",
        details: "Details",
      },
      device_management: {
        title: "Device Management",
        description: "Manage devices that have access to your account.",
        button: "View All Devices",
      },
    },
    data_privacy: {
      data_export: {
        title: "Data Export",
        description: "Download a copy of your personal data.",
        description_text:
          "You can request a copy of all your personal data, including your profile information, activity history, and preferences.",
        button: "Request Data Export",
      },
      privacy_settings: {
        title: "Privacy Settings",
        description: "Control how your data is used and shared.",
        analytics_performance: {
          title: "Analytics & Performance",
          description: "Help us improve by sharing usage data",
        },
        personalized_content: {
          title: "Personalized Content",
          description: "Receive content tailored to your preferences",
        },
      },
      legal_terms: {
        title: "Legal & Terms",
        description: "Review our terms of service and privacy policy.",
        terms_of_service: "Terms of Service",
        privacy_policy: "Privacy Policy",
        cookie_policy: "Cookie Policy",
      },
    },
    subscription: {
      trial_days_left: "days left in trial",
      active_plan: "Active plan",
      no_active_plan: "No active plan",
      processing: "Processing...",
      subscribe: "Subscribe",
      current_plan: {
        title: "Current plan",
        active_until: "Your premium subscription is active until {date}. It will renew automatically.",
        active_until_canceled: "Your premium subscription is active until {date}.",
        next_payment:
          "Next payment date: {date}. You will be charged 59.99€ automatically.",
        trial_active: "You are currently on a trial plan. ",
        trial_ends: "Trial ends in {days} days.",
        trial_expired: "Your trial has expired",
        subscribe_message: "Subscribe to continue using all features",
        canceled_at: "Your subscription has been canceled at:",
      },
      characters: "characters",
    },
    errors: {
      user_id_missing: "User ID is missing",
      personal_details_updated: "Personal details updated successfully",
      personal_details_failed: "Failed to update personal details",
      email_updated: "Email updated successfully",
      email_verification_required:
        "Please check your email to verify the new address",
      email_required: "Email is required",
      invalid_email: "Please enter a valid email address",
      current_password_required: "Current password is required",
      new_password_required: "New password must be at least 12 characters",
      confirm_password_required: "Please confirm your new password",
      passwords_do_not_match: "Passwords do not match",
      password_updated: "Password updated successfully",
      password_update_failed: "Failed to update password",
      account_deleted: "Account deleted successfully",
      account_delete_failed: "Failed to delete account",
      subscription_cancelled: "Subscription cancelled successfully",
      subscription_cancel_failed: "Failed to cancel subscription",
      payment_failed: "Payment failed. Please try again.",
      subscription_failed: "Failed to create subscription portal session",
      subscription_error: "Error creating subscription portal session",
      subscription_portal_failed: "Failed to open subscription portal",
      email_change_failed: "Failed to change email address",
      subscription_management_coming_soon:
        "Subscription management coming soon",
      sms_2fa_setup_coming_soon: "SMS 2FA setup coming soon",
      authenticator_setup_coming_soon: "Authenticator app setup coming soon",
      login_details_coming_soon: "Login details coming soon",
      device_management_coming_soon: "Device management coming soon",
      data_export_requested: "Data export request submitted",
      data_export_failed: "Failed to submit data export request",
    },
    popup: {
      delete_title: "Delete account",
      delete_description:
        "Are you sure you want to permanently delete your account? This action cannot be undone. Deleting your account will also cancel your subscription.",
    },
  },
  login: {
    headline: "Login",
    description: "Enter your account credentials",
    form: {
      label_email: "Email",
      placeholder_email: "user@example.com",
      label_password: "Password",
      placeholder_password: "",
      text_forgot_password: "Forgot password?",
      button_submit: "Login",
      button_loading: "Logging in...",
      text_or: "or",
    google_cta: "Continue with Google",
    apple_cta: "Continue with Apple",      text_no_account: "Don't have an account?",
      text_register: "Register",
    },
    errors: {
      invalid_email: "Enter a valid email address",
      email_required: "Email is required",
      password_required: "Password is required",
      captcha_required: "Please verify that you are not a robot",
      captcha_verification: "Please complete the reCAPTCHA verification",
      invalid_credentials: "Invalid email or password",
    },
    success: {
      email_verified: "Email verified successfully. You can now log in.",
      password_reset:
        "Password reset successful. You can now log in with your new password.",
      email_updated: "Email updated successfully! Please log in with your new email address.",
    },
  },
  register: {
    headline: "Register your 14-day free trial",
    description:
      "Design your year and become best version of yourself with Niumi",
    steps: {
      personal_data: "Register",
      email_confirmation: "Activate account",
    },
    form: {
      placeholder_email: "user@example.com",
      label_email: "Email",
      details_email: "No spam, guaranteed",
      label_password: "Password",
      details_password: "Min 12 characters",
      label_confirm_password: "Confirm password",
      button_submit: "Start my free trial",
      button_resend: "Resend verification email",
      text_or: "or",
    google_cta: "Continue with Google",
    apple_cta: "Continue with Apple",      text_already_registered: "Already have an account?",
      text_login: "Login",
      subheadline_confirm_email: "Verify your email",
      description_confirm_email:
        "Please check your email and click the verification link to complete your registration.",
      terms_text_prefix: "I accept the",
      terms_link: "Terms of Service",
      terms_text_and: "and",
      privacy_link: "Privacy Policy",
    },
    errors: {
      invalid_email: "Enter a valid email address",
      email_required: "Email is required",
      password_min_length: "Password must be at least 12 characters",
      confirm_password_required: "Please confirm your password",
      passwords_do_not_match: "Passwords do not match",
      terms_required: "You must accept the Terms of Service and Privacy Policy",
      captcha_required: "Please verify that you are not a robot",
      registration_failed: "Registration failed. Please try again.",
      verification_email_resent:
        "Verification email has been resent. Please check your inbox.",
      unable_to_resend:
        "Unable to resend verification email. Please try again later.",
    },
    resend_link: {
      headline: "Registration link is expired",
      description:
        "Your registration link has expired. Please request a new one.",
      subheadline: "Request new verification email",
      description_resend:
        "Click the button below to receive a new verification email.",
      button_resend: "Resend verification email",
      form: {
        username: {
          label: "Username",
          placeholder: "Enter your username",
          validation: {
            required: "Username is required",
          },
        },
        email: {
          label: "Email",
          placeholder: "Enter your email",
          validation: {
            invalid: "Please enter a valid email address",
          },
        },
        password: {
          label: "Password",
          placeholder: "Enter your password",
          validation: {
            min_length: "Password must be at least 12 characters",
          },
        },
        confirm_password: {
          label: "Confirm password",
          placeholder: "Confirm your password",
          validation: {
            required: "Please confirm your password",
            match: "Passwords do not match",
          },
        },
      },
    },
  },
  forgot_password: {
    headline: "Reset your password",
    description: "Enter your email so we can send a password reset link",
    expired: {
      headline: "Password reset link expired",
      description:
        "Your password reset link has expired or is invalid. For security reasons, password reset links are only valid for 1 hour. Please request a new link to reset your password.",
      subheadline: "Request a new reset link",
      description_request:
        "Please request a new password reset email. We'll send you a fresh link that will be valid for 1 hour. This will invalidate any previous reset links.",
      button_request: "Request new reset link",
      text_check_spam:
        "Remember to check your spam folder if you don't receive the email.",
      text_security:
        "For security reasons, each new reset link will invalidate any previous reset links.",
    },
    form: {
      subheadline: "Enter your details",
      label_email: "Email",
      placeholder_email: "user@example.com",
      button_reset: "Reset password",
      text_ready: "Ready to start?",
      text_login: "Login",
    },
    errors: {
      email_required: "Email is required",
      invalid_email: "Invalid email format",
      captcha_required: "Please complete CAPTCHA.",
      reset_success: "Check your inbox to reset your password.",
    },
  },
  start_message: {
    headline: "Start your journey!",
    description:
      "This is where your journey begins. Add your first goal and create space for the personal growth.",
    button_get_started: "Get started",
  },
  logout: {
    headline: "Logout",
  },
  help_center: {
    headline: "Help center",
  },
  card_quote: {
    headline: "Quote",
    description: "A quote to inspire and shape your year ahead",
    fallback_text: "No quotes yet. Add your first quote to inspire your year!",
    add_button: "Write a quote",
    form: {
      placeholder: "Write your quote here...",
      label: "Quote",
    },
    popup: {
      delete_title: "Delete",
      delete_description:
        "Are you sure you want to permanently delete this quote?",
      edit_title: "Edit quote",
      add_title: "Write quote for",
    },
    errors: {
      empty: "Quote cannot be empty",
      too_long: "Quote must be 100 characters or less",
      invalid_response: "Invalid response from server",
      submit_error: "Error submitting quote",
      not_found: "Quote not found",
      delete_failed: "Failed to delete quote",
      delete_success: "Quote deleted successfully",
      update_failed: "Failed to update quote",
      update_success: "Quote updated successfully",
    },
  },
  card_theme: {
    headline: "Theme",
    description: "Your main focus area to prioritize this year",
    fallback_text: "No theme set yet. Add your first theme to focus your year!",
    add_button: "Write a theme for",
    popup: {
      delete_title: "Delete",
      delete_description:
        "Are you sure you want to permanently delete this theme?",
      edit_title: "Edit theme",
      add_title: "Write theme for",
    },
    errors: {
      empty: "Theme cannot be empty",
      too_long: "Theme must be 100 characters or less",
      submit_error: "Error submitting theme",
      not_found: "Theme not found",
      delete_failed: "Failed to delete theme",
      delete_success: "Theme deleted successfully",
      update_failed: "Failed to update theme",
      update_success: "Theme updated successfully",
    },
    form: {
      label: "Theme",
      placeholder:
        "Prioritizing growth in my professional life — upskilling, networking, and taking bold steps forward.",
    },
  },
  card_letter: {
    headline: "Letter to your future self",
    description:
      "Write to yourself, letter is sealed until the last day of the",
    open_button: "Open letter in",
    add_button: "Write a letter to your future self",
    popup: {
      delete_title: "Delete",
      delete_description:
        "Are you sure you want to permanently delete this letter?",
      edit_title: "Edit letter",
      add_title: "Write letter to your future self",
      view_title: "Letter to your future self",
    },
    errors: {
      title_too_short: "Title must be at least 2 characters",
      title_too_long: "Title must be 200 characters or less",
      letter_too_short: "Letter must be at least 2 characters",
      letter_too_long: "Letter must be 5000 characters or less",
      submit_failed: "Failed to submit letter",
      submit_success: "Letter submitted successfully!",
      submit_error: "Error submitting letter",
      not_found: "Letter not found",
      delete_failed: "Failed to delete letter",
      delete_success: "Letter deleted successfully",
      update_failed: "Failed to update letter",
      update_success: "Letter updated successfully!",
    },
    form: {
      label_title: "Headline",
      placeholder_title: "How would you name this chapter of your life?",
      label_text: "Letter",
      placeholder_text:
        "What are you proud of today that you hope you'll remember? What do you want to thank your future self for? What struggles are you facing now that you hope you've overcome? What do you hope your future days look like? What is one thing you never want to forget? What advice would you give yourself if you could read this tomorrow? What promise do you want to make to yourself today? What do you want to let go of? What do you hope you're holding on to?",
    },
  },
  card_note: {
    headline: "Journal",
    description: "A space for reflection and growth",
    popup: {
      delete_title: "Delete",
      delete_description:
        "Are you sure you want to permanently delete this note?",
      edit_title: "Edit note",
      add_title: "Write daily note",
      view_title: "View daily note",
    },
    form: {
      label_date: "Date",
      label_title: "Day's highlight",
      placeholder_title: "What made your day?",
      label_text: "Note",
      placeholder_text:
        "What stood out to you today? What gave you energy, and what took it away? What did you notice that you usually overlook? What moment felt most like you? What are you quietly grateful for? What challenged you, and how did you respond? What did you learn about yourself today? What lifted your mood, even if just a little? What would you like to carry into tomorrow? What do you want to let go of from today? What part of today felt meaningful in its own way? What helped you stay grounded? What surprised you emotionally? What unfinished thought deserves more space tomorrow?",
    },
    errors: {
      title_required: "Title is required",
      title_too_long: "Title must be 200 characters or less",
      description_required: "Description is required",
      description_too_long: "Description must be 3000 characters or less",
      date_required: "Date is required",
      submit_failed: "Failed to add note",
      submit_success: "Note added successfully!",
      submit_error: "Error adding note",
      not_found: "Note not found",
      delete_failed: "Failed to delete note",
      delete_success: "Note deleted successfully",
      update_failed: "Failed to update note",
      update_success: "Note updated successfully!",
    },
  },
  card_goal: {
    headline: "Goals for",
    description: "A clear view of where you're headed and how far you've come",
    popup: {
      delete_title: "Delete goal",
      delete_description:
        "Are you sure you want to permanently delete this goal?",
    },
    dropdown: {
      view: "View",
      edit: "Edit",
      delete: "Delete",
    },
  },
  card_progress: {
    headline: "Year progress",
    headline_milestones: "milestones progress", 
    headline_journal: "Transform experience into learnings",
    description_journal: "Reflection connected to your journey",
    description: "See how far you’ve come and what’s left to complete",
    stats: {
      completed: "completed",
      active: "active",
      total: "total",
      streak: "streak",
    },
  },
  card_milestone: {
    headline: "Today’s to-dos",
    description: "Daily tasks, that bring you closer to your goals",
    fallback_text:
      "No tasks planned for {date}. Sit back and relax, you on a right track!",
    popup: {
      delete_title: "Delete",
      delete_description:
        "Are you sure you want to permanently delete this milestone?",
      edit_title: "Edit milestone",
      delete_milestone_title: "Delete Milestone",
      delete_milestone_description:
        "Are you sure you want to delete this milestone?",
    },
    form: {
      label_title: "Title",
      label_deadline: "Deadline",
      label_status: "Completed",
    },
    errors: {
      title_required: "Title is required",
      deadline_required: "Deadline is required",
    },
    toast: {
      marked_completed: "Milestone marked as completed.",
      marked_incomplete: "Milestone marked as incomplete.",
      status_failed: "Failed to update status.",
      updated: "Milestone updated successfully.",
      update_failed: "Error updating milestone.",
      deleted: "Milestone deleted successfully.",
      delete_failed: "Error deleting milestone.",
    },
  },
  card_wishlist: {
    headline: "Wishlist",
    description: "Meaningful rewards for lasting motivation",
    button: "Add wish",
    button_file: "Choose a file",
    popup: {
      delete_title: "Delete",
      delete_description:
        "Are you sure you want to permanently delete this wish?",
      edit_title: "Edit wish",
      add_title: "Add wish",
    },
    form: {
      label_title: "My wish",
      label_img: "Upload an image",
      label_link: "Link",
      placeholder_link: "https://example.com",
      label_price: "Price",
      placeholder_price: "299",
      label_currency: "Currency",
      placeholder_currency: "EUR",
    },
    errors: {
      title_required: "Title is required",
      img_required: "Image is required",
      img_too_large: "Image must be under 5MB",
      url_required: "URL is required",
      url_invalid: "Must be a valid URL",
      price_required: "Price is required",
      price_min: "Price must be greater than 0",
      currency_required: "Currency is required",
      year_required: "Year is required",
    },
    toast: {
      deleted: "Wish deleted successfully!",
      delete_failed: "Failed to delete wish",
      marked_completed: "Wish marked as completed!",
      marked_incomplete: "Wish marked as incomplete!",
      status_failed: "Failed to update wish status.",
      status_error: "Error updating wish status.",
      updated: "Wish updated successfully!",
      update_failed: "Failed to update wish",
      validation_failed: "Validation failed. Please check your input.",
    },
  },
  dropdown: {
    add: "Add",
    edit: "Edit",
    delete: "Delete",
    view: "View",
    view_all: "View all",
    cancel: "Cancel",
    close: "Close",
    save: "Save",
    update: "Update",
    link_shop: "Link to shop",
    bought: "Completed",
    read_more: "Read more",
    keep_subscription: "Keep Subscription",
  },
  tab: {
    title_1: "Goals & progress",
    title_2: "Milestones & progress",
    title_3: "Journal",
    title_4: "Wishlist",
  },
  metrics: {
    total: "Total goals",
    progress: "In process",
    completed: "Completed",
    fav: "My main focus for",
    total_desc: "Total goals for {year}",
    progress_desc: "Goals in progress for {year}",
    completed_desc: "Completed goals for {year}",
    popup: {
      edit_title: "Edit my main focus for",
      edit_description:
        "Choose one ultimate focus for the year and make it your priority",
    },
  },
  calendar: {
    upcoming: "Upcoming",
    completed: "Completed",
    today: "Today",
    mon: "Mon",
    tue: "Tue",
    wed: "Wed",
    thu: "Thu",
    fri: "Fri",
    sat: "Sat",
    sun: "Sun",
  },
  addgoal: {
    toggle_title: "How to set your S.M.A.R.T goals?",
    milestone_title: "Milestones",
    milesone_description: "Break down your goal into practical tasks",
    button_milestone: "+ Add milestone",
    button_add_goal: "Add new goal",
    button_update_goal: "Update the goal",
    button_updating_goal: "Updating the goal...",
    smart: {
      s: "Specific - Clear and focused",
      m: "Measurable - Track progress",
      a: "Achievable - Realistic and attainable",
      r: "Relevant - Aligned with vision",
      t: "Time-bound - Deadline driven",
      help: 'Need help writing your goals? Visit our <a href="https://www.niumi.dk/blog/" target="_blank" rel="noopener noreferrer" class="text-primary underline">resources</a> for tips, examples, and guided support.',
    },
    form: {
      label_title: "Set goal",
      label_img: "Upload goal image",
      label_deadline: "Yearly timeline",
      details_goal_title: "{count}/100 characters",
      details_goal_year: "{count}/4 characters",
      label_category: "Select category",
      details_goal_category: "{count}/50 characters",
      label_milestone_title: "Milestone",
      placeholder_milestone_title: "Book tickets",
      details_milestone_title: "{count}/100 characters",
      label_milestone_deadline: "Deadline",
      details_milestone_deadline: "{count}/10 characters",
      label_milestone_repeat: "Repeat",
      label_milestone_repeat_until: "until",
      label_milestone_repeat_interval: "Repeat interval",
      label_milestone_select: "Select...",
      label_milestone_repeat_1: "Daily",
      label_milestone_repeat_2: "Weekly",
      label_milestone_repeat_3: "Monthly",
      label_milestone_repeat_4: "Quarterly",
      details_milestone_repeat_until: "{count}/10 characters",
    },
    errors: {
      milestone_title_required: "Milestone title is required",
      milestone_deadline_required: "Deadline is required",
      milestone_repeat_interval_required:
        "Repeat interval is required when repeat is checked",
      milestone_repeat_until_required:
        "End date is required when repeat is checked and interval is selected",
      goal_title_required: "Goal title is required",
      goal_img_required: "Image is required",
      goal_img_too_large: "Image must be under 5MB",
      goal_year_invalid: "Enter a valid year",
      goal_year_integer: "Year must be an integer",
      goal_year_4_digits: "Enter a valid 4-digit year",
      goal_category_required: "Category is required",
      milestones_required: "At least one milestone is required",
    },
  },
  pricing: {
    headline: "Pricing",
    description: "We like to keep things simple. One plan one price.",
    plan_name: "Niumi Plan",
    plan_desc: "Billed annually.",
    subscribe_cta: "Subscribe",
    subscription_success: "Subscription activated successfully!",
    subscription_cancelled: "Subscription checkout was cancelled.",
    features: {
      unlimited_goals: "Unlimited goals and milestones",
      progress_dashboard: "Progress dashboard overview",
      daily_reminders: "Daily notification reminders",
      vision_board: "Personalised and downloadable vision board",
      wishlist: "Custom wishlist with shareable link",
      journaling: "Journaling and reflection tool",
      calendar: "Built-in calendar and task planning",
      personalised_focus:
        "Personalised focus for a year with: theme, quote, and letter to future self",
    },
  },
  settings: {
    headline: "Settings",
  },
  loading: "Loading...",
  card_milestones: {
    errors: {
      no_milestone_selected: "No milestone selected for deletion",
      delete_failed: "Failed to delete milestone",
      delete_success: "Milestone deleted successfully",
      no_milestone_edit: "No milestone selected for editing",
      update_failed: "Failed to update milestone",
      update_success: "Milestone updated successfully",
      status_update_success: "Milestone marked as completed",
      status_update_incomplete: "Milestone marked as incomplete",
      status_update_failed: "Failed to update status",
    },
    messages: {
      past_year:
        "You're viewing a past year, a chapter already written in your story.",
      future_year:
        "No tasks planned for this future. This space is yours to shape.",
      no_tasks:
        "No tasks planned for {date}. Sit back and relax, you on a right track!",
    },
  },
  reset_password: {
    headline: "Reset password",
    description: "Please enter your new password.",
    form: {
      password: {
        label: "Password",
        placeholder: "New password",
        details: "Min 12 characters.",
        validation: {
          min_length: "Password must be at least 12 characters",
          uppercase: "Password must contain at least one uppercase letter",
          lowercase: "Password must contain at least one lowercase letter",
          number: "Password must contain at least one number",
          special: "Password must contain at least one special character",
        },
      },
      confirm_password: {
        label: "Confirm Password",
        placeholder: "Confirm new password",
        validation: {
          required: "Please confirm your password",
          match: "Passwords do not match",
        },
      },
      submit: "Reset Password",
    },
    login: {
      text: "Ready to start?",
      link: "Login",
    },
    success: "Password reset successful. Redirecting to login...",
    error: {
      invalid_token: "Invalid or expired reset token.",
    },
  },
  email_change: {
    expired: {
      headline: "Email change link expired",
      description: "Your email change verification link has expired.",
      subheadline: "Try again",
      description_request:
        "Please go back to your profile and request the email change again.",
      button_profile: "Back to Profile",
    },
  },
  public_wishlist: {
    headline: "{name}'s wishlist",
    headline_fallback: "Wishlist",
    loading: "Loading...",
    empty: {
      title: "No wishes found",
      description: "This wishlist is empty.",
    },
  },
  finance: {
    headline: "Finance Tracker",
    description: "Track your budget with the 50/30/20 rule to stay balanced and build steady financial growth.",
    tooltips: {
      income: {
        title: "Income",
        description: "Your total monthly income from all sources including salary, freelance work, investments, and other revenue streams."
      },
      needs: {
        title: "Needs",
        description: "Essential expenses that you must pay to maintain your basic lifestyle. These include housing, utilities, groceries, transportation, insurance, and minimum debt payments."
      },
      wants: {
        title: "Wants",
        description: "Non-essential expenses that enhance your lifestyle but aren't necessary for survival. These include dining out, entertainment, hobbies, luxury items, and optional subscriptions."
      },
      savings: {
        title: "Savings & Investments",
        description: "Money set aside for future goals, emergencies, and wealth building. This includes emergency funds, retirement savings, investment accounts, and other long-term financial goals."
      },
      budget_allocation: {
        title: "Budget allocation",
        description: "This shows how your income is distributed across different categories. The 50/30/20 rule suggests allocating 50% to needs, 30% to wants, and 20% to savings and investments."
      },
      goal_percentages: {
        title: "GOAL pERCENTAGES",
        description: "These are your target percentages for budget allocation. You can edit them by clicking on the input fields. Needs are essential expenses like rent, food, and utilities. Savings & Investments are money set aside for future goals. Wants are non-essential spending like entertainment and dining out. The percentages should add up to 100% for optimal budget management."
      },
      actual_percentages: {
        title: "ACTUAL pERCENTAGES", 
        description: "These show your real spending percentages based on your current allocations. They are calculated from your actual spending amounts divided by total income. Red percentages indicate you are spending more than your goal for that category. Green percentages or no color mean you are within your target range. This helps you see how well you are following your budget goals."
      }
    }
  },
  sidebar: {
    upgrade_plan: {
      title: "Upgrade your plan",
      description:
        "Your trial ends in {days} days. Upgrade your plan and unlock full potential!",
      button: "See plans",
    },
    settings: "Settings",
  },
};

export default en;
